import React, { useEffect, useState, useCallback } from 'react';
import { Box, Text, VStack, HStack, Spinner, useDisclosure, Card } from '@chakra-ui/react';
import { BenefitCard } from '../../ModulesSuperApp/components/BenefitCard';
import { images, planTypes } from './atoms/utils';
import { ModalBuyModule } from './atoms/ModalBuyModule.tsx';
import { AgreementKey } from './atoms/utils';
import { Product } from './interfaces';
import { useTheme } from '@partiu/ui/contexts/ThemeProvider.tsx';

interface Step0PartiuListProps {
  productsV2: Product[];
  isLoading?: boolean;
}

// Configurações das seções de produtos
const PRODUCT_SECTIONS = {
  digitalLibrary: {
    title: 'Biblioteca digital',
    products: ['Literários', 'AudioBook', 'Profissional', 'Educa', 'Infantil'] as AgreementKey[]
  },
  newsstand: {
    title: 'Banca',
    products: ['Revista Nacionais', 'Revista Digital de Descontos'] as AgreementKey[]
  },
  superApp: {
    title: 'Super App',
    products: ['Super App'] as AgreementKey[]
  },
  advantagesClub: {
    title: 'Clube de Vantagens',
    products: ['Ecommerce', 'Cinemas', 'Lojas'] as AgreementKey[]
  }
} as const;

// Estado inicial para todos os acordos
const INITIAL_AGREEMENTS_STATE: Record<AgreementKey, boolean> = {
  'Super App': false,
  'AudioBook': false,
  'Revista': false,
  'E-Book': false,
  'Literários': false,
  'Profissional': false,
  'Educa': false,
  'Infantil': false,
  'Revista Nacionais': false,
  'Revista Digital de Descontos': false,
  'Ecommerce': false,
  'Cinemas': false,
  'Lojas': false,
};

// Hook customizado para gerenciar acordos ativos
const useActiveAgreements = (products: Product[]) => {
  const [activeAgreements, setActiveAgreements] = useState<Record<AgreementKey, boolean>>(INITIAL_AGREEMENTS_STATE);

  useEffect(() => {
    const next = { ...INITIAL_AGREEMENTS_STATE };

    products.forEach((product) => {
      if (product?.default_product?.is_superapp) {
        next['Super App'] = true;
        if(product.metadata?.ecommerceModule?.value === 'true') {
          next['Ecommerce'] = true;
          next['Cinemas'] = true;
          next['Revista Digital de Descontos'] = true;
        }
        if(product.metadata?.localModule?.value === 'true') {
          next['Lojas'] = true;
        }
      }
      if(product.metadata?.sectors?.value?.includes('1')) {
        next['Literários'] = true;
      }
      if(product.metadata?.hasAudioBook?.value === 'true') {
        next['AudioBook'] = true;
      }
      if(product.metadata?.sectors?.value?.includes('2')) {
        next['Profissional'] = true;
      }
      if(product.metadata?.sectors?.value?.includes('4')) {
        next['Educa'] = true;
      }
      if(product.metadata?.sectors?.value?.includes('5')) {
        next['Infantil'] = true;
      }
      if(product.metadata?.hasMagazine?.value === 'true') {
        next['Revista Nacionais'] = true;
      }
    });

    setActiveAgreements(next);
  }, [products]);

  return activeAgreements;
};

// Hook customizado para gerenciar seleção de produtos
const useProductSelection = () => {
  const [selectedProducts, setSelectedProducts] = useState<Record<AgreementKey, boolean>>(INITIAL_AGREEMENTS_STATE);

  const toggleProductSelection = useCallback((key: AgreementKey) => {
    setSelectedProducts(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  }, []);

  return { selectedProducts, toggleProductSelection };
};

// Componente para seção de produtos
interface ProductSectionProps {
  title: string;
  products: AgreementKey[];
  activeAgreements: Record<AgreementKey, boolean>;
  selectedProducts: Record<AgreementKey, boolean>;
  onProductToggle: (key: AgreementKey) => void;
  colors: {
    primaryColor: string;
  };
}

const ProductSection: React.FC<ProductSectionProps> = React.memo(({
  title,
  products,
  activeAgreements,
  selectedProducts,
  onProductToggle,
  colors
}) => (
  <Box
    width="100%"
    role="region"
    aria-labelledby={`section-${title.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <Text
      fontSize="sm"
      mb={2}
      fontWeight="medium"
      id={`section-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {title}
    </Text>
    <HStack
      spacing={4}
      flexWrap="wrap"
      role="list"
      aria-label={`Lista de produtos da seção ${title}`}
    >
      {products.map((key) => {
        // Map AgreementKey to the correct keys for planTypes and images
        let planTypeKey: string = key;
        let imageKey: string = key;

        if (key === 'Revista') {
          planTypeKey = 'Revista Nacionais';
          imageKey = 'Revista Nacionais';
        } else if (key === 'AudioBook') {
          planTypeKey = 'Audio Book';
          imageKey = 'Audio Book';
        }

        return (
          <BenefitCard
            key={key}
            title={planTypes[planTypeKey as keyof typeof planTypes]}
            image={images[imageKey as keyof typeof images]}
            selected={!!selectedProducts[key]}
            disabled={!activeAgreements[key]}
            color={colors.primaryColor}
            small
            partiuPlan
            onClick={() => onProductToggle(key)}
          />
        )
      })}
    </HStack>
  </Box>
));

ProductSection.displayName = 'ProductSection';

export const Step0PartiuList: React.FC<Step0PartiuListProps> = ({ productsV2 = [], isLoading }) => {
  const { colors } = useTheme();
  const activeAgreements = useActiveAgreements(productsV2);
  const { selectedProducts, toggleProductSelection } = useProductSelection();
  const modalHabilitar = useDisclosure();

  const handleProductToggle = useCallback((key: AgreementKey) => {
    if (activeAgreements[key]) {
      toggleProductSelection(key);
    } else {
      modalHabilitar.onOpen();
    }
  }, [activeAgreements, toggleProductSelection, modalHabilitar]);

  const renderLoadingState = () => (
    <Box
      textAlign="center"
      py={8}
      role="status"
      aria-live="polite"
    >
      <Spinner
        size="lg"
        color="blue.500"
        aria-label="Carregando produtos"
      />
      <Text mt={4} color="gray.600">
        Carregando produtos...
      </Text>
    </Box>
  );

  const renderProductSections = () => (
    <VStack spacing={4} width="100%" role="main" aria-label="Produtos do programa Partiu">
      {/* Card com instruções */}
      <Card
        overflowY="auto"
        gap="10px"
        alignItems="center"
        padding={4}
        width={{ base: '100%' }}
      >
        <Text textAlign="center" fontSize="md" fontWeight="bold" color="#2D3748" mb={3}>
          Configure seu Super APP PARTIU! 🚀
        </Text>

        <Box width="100%" display="flex" flexDirection="column" gap="6px">
          <Box bg="blue.50" p={3} borderRadius="md" borderLeft="4px solid" borderLeftColor="blue.400">
            <Text fontSize="13px" lineHeight="1.5" color="gray.700" mb={2}>
              Personalize a experiência dos seus clientes com o <Text as="span" fontWeight="bold" color="blue.600">PARTIU</Text>:
            </Text>

            <VStack align="start" spacing={1}>
              <Text fontSize="13px" lineHeight="1.3" color="gray.700">
                <Text as="span" fontWeight="bold" color="blue.600">1.</Text> Disponibilize apps integrados com a identidade visual da sua empresa
              </Text>
              <Text fontSize="13px" lineHeight="1.3" color="gray.700">
                <Text as="span" fontWeight="bold" color="blue.600">2.</Text> Selecione benefícios relevantes para seu público
              </Text>
              <Text fontSize="13px" lineHeight="1.3" color="gray.700">
                <Text as="span" fontWeight="bold" color="blue.600">3.</Text> Gerencie tudo de forma intuitiva e otimizada
              </Text>
            </VStack>
          </Box>

          <Box bg="orange.50" p={3} borderRadius="md" borderLeft="4px solid" borderLeftColor="orange.400">
            <Text fontSize="13px" fontWeight="semibold" color="orange.800" mb={1.5}>
              Antes de começar:
            </Text>

            <VStack align="start" spacing={1}>
              <Text fontSize="13px" lineHeight="1.3" color="gray.700">
                <Text as="span" fontWeight="bold" color="orange.600">•</Text> Configurações ficam disponíveis em até{' '}
                <Text as="span" fontWeight="bold" color="green.600">24h</Text>
              </Text>
              <Text fontSize="13px" lineHeight="1.3" color="gray.700">
                <Text as="span" fontWeight="bold" color="orange.600">•</Text> Adicione novos apps personalizados quando quiser
              </Text>
              <Text fontSize="13px" lineHeight="1.3" color="gray.700">
                <Text as="span" fontWeight="bold" color="orange.600">•</Text> Edite e ajuste a qualquer momento
              </Text>
            </VStack>
          </Box>

          <Box bg="green.50" p={2.5} borderRadius="md" textAlign="center" border="1px solid" borderColor="green.200">
            <Text fontSize="13px" fontWeight="medium" color="green.800" lineHeight="1.3">
              Processo simples e intuitivo. Precisa de ajuda? Estamos aqui! ✨
            </Text>
          </Box>
        </Box>
      </Card>

      {/* Primeira linha: Biblioteca digital e Banca lado a lado */}
      <HStack spacing={4} width="100%" align="stretch" direction={{ base: 'column', md: 'row' }}>
        <Box flex="0 0 70%">
          <ProductSection
            title={PRODUCT_SECTIONS.digitalLibrary.title}
            products={PRODUCT_SECTIONS.digitalLibrary.products}
            activeAgreements={activeAgreements}
            selectedProducts={selectedProducts}
            onProductToggle={handleProductToggle}
            colors={colors}
          />
        </Box>
        <Box flex="0 0 30%">
          <ProductSection
            title={PRODUCT_SECTIONS.newsstand.title}
            products={PRODUCT_SECTIONS.newsstand.products}
            activeAgreements={activeAgreements}
            selectedProducts={selectedProducts}
            onProductToggle={handleProductToggle}
            colors={colors}
          />
        </Box>
      </HStack>

      {/* Segunda linha: Clube de Vantagens e Super App lado a lado */}
      <HStack spacing={4} width="100%" align="stretch" direction={{ base: 'column', md: 'row' }}>
        <Box flex="0 0 70%">
          <ProductSection
            title={PRODUCT_SECTIONS.advantagesClub.title}
            products={PRODUCT_SECTIONS.advantagesClub.products}
            activeAgreements={activeAgreements}
            selectedProducts={selectedProducts}
            onProductToggle={handleProductToggle}
            colors={colors}
          />
        </Box>
        <Box flex="0 0 30%">
          <ProductSection
            title={PRODUCT_SECTIONS.superApp.title}
            products={PRODUCT_SECTIONS.superApp.products}
            activeAgreements={activeAgreements}
            selectedProducts={selectedProducts}
            onProductToggle={handleProductToggle}
            colors={colors}
          />
        </Box>
      </HStack>

      <ModalBuyModule modalHabilitar={modalHabilitar} />
    </VStack>
  );

  if (isLoading) {
    return renderLoadingState();
  }

  return renderProductSections();
};

export default Step0PartiuList;
